import * as React from "react";
import { cn } from "@/lib/utils";

const cardVariants = {
  default: "shadow-[var(--card-shadow)] border-border bg-card",
  glass: "bg-glass",
  interactive:
    "shadow-[var(--card-shadow)] border-border bg-card cursor-pointer hover:shadow-[var(--card-shadow-hover)] hover:-translate-y-px hover:border-border/80 transition-all duration-200 group relative",
  muted: "shadow-[var(--card-shadow)] border-border bg-muted/50",
  "muted-solid": "shadow-[var(--card-shadow)] border-border bg-muted",
  "muted-subtle": "shadow-[var(--card-shadow)] border-border bg-muted/30",
  "primary-muted": "shadow-[var(--card-shadow)] border-border bg-primary-muted",
  "success-muted": "shadow-[var(--card-shadow)] border-border bg-success-muted",
  "info-muted": "shadow-[var(--card-shadow)] border-border bg-info-muted",
  "message-user":
    "bg-chat-bubble-user text-chat-bubble-user-foreground shadow-md border-0 rounded-tr-none",
  "message-assistant":
    "bg-chat-bubble-assistant text-chat-bubble-assistant-foreground border-0 shadow-sm rounded-tl-none",
  "chat-messages": "bg-chat-canvas border-0 shadow-none rounded-none",
  "chat-input": "bg-muted border-0 shadow-none rounded-none",
  "chat-section": "bg-muted/50 border-0 shadow-none",
  overlay: "bg-card/70 border-0 shadow-none",
  "muted-inline": "bg-muted border-0 shadow-none rounded-lg",
  "muted-footer": "bg-muted border-t border-border shadow-none rounded-b-lg",
  "muted-sidebar": "bg-muted border-l border-border shadow-none",
} as const;

const cardPaddingVariants = {
  default: "gap-6 py-6 px-6",
  compact: "gap-4 p-4",
  none: "gap-0 p-0",
} as const;

interface CardProps extends React.ComponentProps<"div"> {
  variant?: keyof typeof cardVariants;
  padding?: keyof typeof cardPaddingVariants;
  overflowHidden?: boolean;
}

function Card({
  className,
  variant = "default",
  padding = "default",
  overflowHidden = false,
  ...props
}: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(
        "text-card-foreground flex min-w-0 flex-col rounded-xl border",
        cardVariants[variant],
        cardPaddingVariants[padding],
        overflowHidden && "overflow-hidden",
        className
      )}
      {...props}
    />
  );
}

const cardHeaderVariants = {
  default:
    "grid min-w-0 auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[minmax(0,1fr)_auto] [.border-b]:pb-6",
  bar: "shrink-0 border-b border-border bg-card shadow-sm flex flex-row items-center justify-between gap-3 px-4 sm:px-6 py-3",
  glass:
    "shrink-0 border-b border-border bg-glass flex flex-row items-center justify-between gap-3 px-4 sm:px-6 py-3",
} as const;

function CardHeader({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  variant?: keyof typeof cardHeaderVariants;
}) {
  return (
    <div
      data-slot="card-header"
      className={cn(cardHeaderVariants[variant], className)}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "min-w-0 leading-snug font-semibold [overflow-wrap:anywhere]",
        className
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("min-w-0 px-6", className)}
      {...props}
    />
  );
}

const cardFooterVariants = {
  default: "flex items-center px-6 [.border-t]:pt-6",
  compact: "flex flex-row items-center p-3 border-t border-border bg-muted/50",
} as const;

function CardFooter({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  variant?: keyof typeof cardFooterVariants;
}) {
  return (
    <div
      data-slot="card-footer"
      className={cn(cardFooterVariants[variant], className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
